/**
 * Estadísticas de moderación de un cliente (lib/moderation-stats en la API): el resumen del
 * período, lo que espera a una persona con su motivo, y en qué publicaciones se conversa.
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";

export interface StatsCounts {
  received: number;
  answeredByBot: number;
  answeredByHuman: number;
  waiting: number;
  notNeeded: number;
  pending: number;
}

export interface StatsMessage {
  id: number;
  conversationId: string | null;
  platform: string;
  kind: string;
  username: string | null;
  body: string;
  attachment: string | null;
  state: string;
  outcome: keyof StatsCounts;
  reason: string | null;
  response: string | null;
  receivedAt: string;
  respondedAt: string | null;
  rationale?: string | null;
}

export interface ModerationStats {
  range: { from: string; to: string; days: number };
  totals: StatsCounts;
  byPlatform: Record<string, StatsCounts>;
  byKind: Record<string, StatsCounts>;
  escalationReasons: Array<{ key: string; count: number }>;
  mentions: number;
  botResponseMinutes: number | null;
  daily: Array<StatsCounts & { date: string }>;
  topics: Array<{ term: string; count: number }>;
  waiting: StatsMessage[];
}

export interface PostStat extends StatsCounts {
  id: string;
  platform: string;
  caption: string | null;
  permalink: string | null;
  imageUrl: string | null;
  publishedAt: string | null;
  theme: string | null;
  brands: string[];
  reasons: Array<{ key: string; count: number }>;
  messages: StatsMessage[];
}

export interface PostGroup extends StatsCounts {
  name: string;
  posts: number;
}

export interface PostStats {
  range: { from: string; to: string; days: number };
  posts: PostStat[];
  byTheme: PostGroup[];
  byBrand: PostGroup[];
  totalPosts: number;
  commentsWithoutPost: number;
}

export function useModerationStats(clientSlug: string | undefined, days: number) {
  return useQuery<ModerationStats>({
    queryKey: ["moderation-stats", clientSlug, days],
    enabled: !!clientSlug,
    queryFn: () => apiGet<ModerationStats>(`/monitoring/stats?clientSlug=${encodeURIComponent(clientSlug!)}&days=${days}`),
  });
}

export function usePostStats(clientSlug: string | undefined, days: number) {
  return useQuery<PostStats>({
    queryKey: ["post-stats", clientSlug, days],
    enabled: !!clientSlug,
    // La primera vez le pregunta a Meta por cada comentario: puede tardar.
    staleTime: 5 * 60 * 1000,
    queryFn: () => apiGet<PostStats>(`/monitoring/posts?clientSlug=${encodeURIComponent(clientSlug!)}&days=${days}`),
  });
}
